import React, { useState, useEffect } from 'react'
import { 
  Plus,
  Download,
  ArrowLeft,
  ChevronRight,
  Home
} from 'lucide-react'
import DataTable from '../../components/DataTable/DataTable'
import Breadcrumb from '../../components/Common/Breadcrumb'
import autoIncrementService from '../../services/autoIncrementService'
import { toast } from 'react-toastify'
import { useNavigate } from 'react-router-dom'
// Consolidated CSS is imported globally

const AutoIncrementMaster = () => {
  const navigate = useNavigate()
  const [selectedRows, setSelectedRows] = useState([])
  const [data, setData] = useState([])
  const [loading, setLoading] = useState(true)
  const [stats, setStats] = useState({
    totalSequences: 0,
    activeSequences: 0,
    inactiveSequences: 0
  })

  // Load data from backend
  useEffect(() => {
    loadData()
  }, [])

  const loadData = async () => {
    try {
      setLoading(true)
      // Fetch all sequences by passing a very high limit to get all records
      // The backend defaults to limit=10, so we need to explicitly pass a high limit
      const params = { page: 1, limit: 10000 }
      const [sequencesResponse, statsResponse] = await Promise.all([
        autoIncrementService.getAllSequences(params),
        autoIncrementService.getSequenceStats()
      ])
      
      if (sequencesResponse.success) {
        // Transform _id to id for frontend compatibility
        const transformedData = (sequencesResponse.data || []).map(sequence => ({
          ...sequence,
          id: sequence._id
        }))
        console.log(`Loaded ${transformedData.length} sequences (expected 38)`)
        setData(transformedData)
        if (transformedData.length === 0) {
          toast.info('No auto increment sequences found. Create your first sequence!')
        }
      } else {
        toast.error(sequencesResponse.message || 'Failed to load sequences')
      }
      
      if (statsResponse.success) {
        setStats(statsResponse.data)
      } else {
        toast.error(statsResponse.message || 'Failed to load statistics')
      }
    } catch (error) {
      console.error('Error loading auto increment sequences:', error)
      if (error.response?.status === 401) {
        toast.error('Authentication required. Please log in again.')
      } else if (error.response?.status === 403) {
        toast.error('You do not have permission to view sequences')
      } else {
        toast.error('Failed to load auto increment sequences')
      }
    } finally {
      setLoading(false)
    }
  }

  const columns = [
    {
      accessorKey: 'sequenceName',
      header: 'Sequence Name',
      cell: ({ getValue }) => (
        <div className="sequence-name-cell" style={{ textAlign: 'left' }}>
          <span className="name-text">{getValue()}</span>
        </div>
      ),
    },
    {
      accessorKey: 'description',
      header: 'Description',
      cell: ({ getValue }) => (
        <div className="description-cell" style={{ textAlign: 'left' }}>
          <span className="description-text">{getValue()}</span>
        </div>
      ),
    },
    {
      accessorKey: 'currentValue',
      header: 'Current Value',
      cell: ({ getValue }) => (
        <div className="value-cell" style={{ textAlign: 'center' }}>
          <span className="value-text">{getValue() || '-'}</span>
        </div>
      ),
    },
    {
      accessorKey: 'prefix',
      header: 'Prefix',
      cell: ({ getValue }) => (
        <div className="prefix-cell" style={{ textAlign: 'center' }}>
          <span className="prefix-text">{getValue() || '-'}</span>
        </div>
      ),
    },
    {
      accessorKey: 'digits',
      header: 'Digits',
      cell: ({ getValue }) => (
        <div className="digits-cell" style={{ textAlign: 'center' }}>
          <span className="digits-text">{getValue() || '4'}</span>
        </div>
      ),
    },
    {
      accessorKey: 'incrementBy',
      header: 'Increment By',
      cell: ({ getValue }) => (
        <div className="increment-cell" style={{ textAlign: 'center' }}>
          <span className="increment-text">{getValue()}</span>
        </div>
      ),
    },
    {
      accessorKey: 'status',
      header: 'Status',
      cell: ({ getValue }) => (
        <div className="status-cell" style={{ textAlign: 'center' }}>
          <span className={`status-badge ${getValue().toLowerCase()}`}>
            {getValue()}
          </span>
        </div>
      ),
    },
  ]

  const handleAdd = () => {
    navigate('/settings/auto-increment-master/form')
  }

  const handleEdit = (sequenceData) => {
    navigate('/settings/auto-increment-master/edit', { state: { sequenceData } })
  }

  const handleView = (sequenceData) => {
    navigate('/settings/auto-increment-master/view', { state: { sequenceData } })
  }

  const handleDelete = async (sequenceData) => {
    try {
      toast.info('Deleting sequence...')
      const response = await autoIncrementService.deleteSequence(sequenceData.id)
      if (response.success) {
        toast.success('Sequence deleted successfully!', { autoClose: 2000 })
        loadData() // Reload data
      } else {
        toast.error(response.message || 'Failed to delete sequence')
      }
    } catch (error) {
      console.error('Error deleting sequence:', error)
      if (error.response?.status === 404) {
        toast.error('Sequence not found')
      } else if (error.response?.status === 403) {
        toast.error('You do not have permission to delete this sequence')
      } else {
        toast.error('Failed to delete sequence. Please try again.')
      }
    }
  }

  const handleExport = () => {
    // TODO: Implement export functionality
    toast.info('Export functionality coming soon!')
  }

  const breadcrumbItems = [
    {
      label: 'Settings',
      path: '/settings',
      onClick: () => navigate('/settings')
    },
    {
      label: 'Transactions',
      path: '/settings?tab=transactions',
      onClick: () => navigate('/settings?tab=transactions')
    },
    {
      label: 'Auto Increment Master',
      path: '/settings/auto-increment-master',
      isCurrent: true
    }
  ]

  return (
    <div className="sales-dashboard">
      {/* Breadcrumb Navigation */}
      <div className="breadcrumb-section">
        <Breadcrumb items={breadcrumbItems} />
      </div>

      {/* Page Header */}
      <div className="page-header">
        <div className="page-title">
          <h1>Auto Increment Master</h1>
          <p>Manage auto-increment sequences and counters</p>
        </div>
        <div className="page-actions">
          <button className="btn btn-primary" onClick={handleAdd}>
            <Plus size={16} />
            Add
          </button>
        </div>
      </div>

      {/* Metrics Section */}
      <div className="dashboard-metrics-grid">
        <div className="metric-card revenue">
          <div className="metric-header">
            <div className="metric-icon">
              <span>{stats.totalSequences}</span>
            </div>
            <div className="metric-trend positive">
              <span>Total</span>
            </div>
          </div>
          <div className="metric-value">{stats.totalSequences}</div>
          <div className="metric-title">Total Sequences</div>
          <div className="metric-subtitle">All sequences</div>
        </div>

        <div className="metric-card customers">
          <div className="metric-header">
            <div className="metric-icon">
              <span>{stats.activeSequences}</span>
            </div>
            <div className="metric-trend positive">
              <span>Active</span>
            </div>
          </div>
          <div className="metric-value">{stats.activeSequences}</div>
          <div className="metric-title">Active Sequences</div>
          <div className="metric-subtitle">Currently active</div>
        </div>

        <div className="metric-card churn">
          <div className="metric-header">
            <div className="metric-icon">
              <span>{stats.inactiveSequences}</span>
            </div>
            <div className="metric-trend negative">
              <span>Inactive</span>
            </div>
          </div>
          <div className="metric-value">{stats.inactiveSequences}</div>
          <div className="metric-title">Inactive Sequences</div>
          <div className="metric-subtitle">Different sequence types</div>
        </div>
      </div>

      {/* Data Table */}
      <DataTable
        data={data}
        columns={columns}
        loading={loading}
        onView={handleView}
        onEdit={handleEdit}
        onDelete={handleDelete}
        onExport={handleExport}
        noCard={true}
        className="auto-increment-master-table"
        pageSize={10}
      />
    </div>
  )
}

export default AutoIncrementMaster